import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  hcGetCrqsWithConflicts,
  hcGetCrqsToAddActivity,
  hcPollCrqsWithConflicts,
  hcPollCrqsToAddActivity,
  hcCrqsWithConflictsAction,
  hcCrqsToAddActivityAction,
  hcExportCrqsOther,
  hcGetBotSummary,
  hcPollBotSummary,
} from '../actions/helixCrqsThunks';
import './HelixCrqs.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
  X: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  ChevronDown: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  Clock: ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatTimestamp = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

// ─── MESSAGE MODAL ────────────────────────────────────────────────────────────
function MessageModal({ message, onClose }) {
  return createPortal(
    <div className="hc-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="hc-modal">
        <div className="hc-modal-header">
          <p className="hc-modal-title">Message</p>
          <button className="hc-close-btn" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="hc-modal-body">
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: "'Inter', sans-serif" }}>
            {message}
          </p>
        </div>
        <div className="hc-modal-footer">
          <button className="hc-btn hc-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── CHANGE BOT DROPDOWN ──────────────────────────────────────────────────────
const BOT_OPTIONS = [1, 2, 3, 4, 5];

function ChangeBotDropdown({ selectedIds, onChangeBot, disabled }) {
  const [open, setOpen] = useState(false);
  const ref             = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="hc-dropdown" ref={ref}>
      <button
        className="hc-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || selectedIds.length === 0}
      >
        Change Bot
        {selectedIds.length > 0 && <span className="hc-dropdown-count">{selectedIds.length}</span>}
        <Icon.ChevronDown />
      </button>
      {open && (
        <div className="hc-dropdown-menu">
          {BOT_OPTIONS.map((bot) => (
            <button key={bot} className="hc-dropdown-item" onClick={() => { setOpen(false); onChangeBot(bot); }}>
              <span className="hc-dropdown-item-icon" style={{ color: '#7c3aed' }}>⬡</span>
              Bot {bot}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── HISTORY TOGGLE ───────────────────────────────────────────────────────────
function HistoryToggle({ value, onChange }) {
  return (
    <button
      className={`hc-history-toggle${value ? ' hc-history-toggle--active' : ''}`}
      onClick={() => onChange(!value)}
      title={value ? 'Showing history — click to hide' : 'Show history'}
    >
      <Icon.Clock size={13} color={value ? '#7c3aed' : '#94a3b8'} />
      History
    </button>
  );
}

// ─── CRQ TABLE ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const searchMatch = (row, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  return [
    row.crqChangeId, row.message, row.user, row.bot, row.status,
  ].some((v) => v != null && String(v).toLowerCase().includes(s));
};

function CrqTable({ rows = [], loading, onMessageClick, selected = [], onToggle, onToggleAll, extraColumns = [], clickableKeys = [], hideUser = false }) {
  const [page,   setPage]   = useState(1);
  const [search, setSearch] = useState('');

  useEffect(() => { setPage(1); }, [rows.length, search]);

  const filtered   = rows.filter((r) => searchMatch(r, search));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const allSelected  = paged.length > 0 && paged.every((r) => selected.some((x) => x.id === r.id));
  const someSelected = !allSelected && paged.some((r) => selected.some((x) => x.id === r.id));
  const colSpan      = (hideUser ? 9 : 10) + extraColumns.length;

  return (
    <div>
      <div className="hc-search-bar">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input className="hc-search-input" type="text" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button className="hc-search-clear" onClick={() => setSearch('')}>✕</button>}
      </div>
      <div className="hc-table-wrapper">
        <table className="hc-table">
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: 'center' }}>
                <input type="checkbox" className="hc-checkbox" checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={() => onToggleAll(paged)} disabled={loading || paged.length === 0}
                />
              </th>
              <th>CRQ Change ID</th>
              {extraColumns.map((col) => <th key={col.key}>{col.label}</th>)}
              <th style={{ textAlign: 'center' }}>Warning</th>
              <th style={{ textAlign: 'center' }}>Error</th>
              <th>Message</th>
              <th>RPA Start</th>
              <th>RPA End</th>
              {!hideUser && <th>User</th>}
              <th>Bot</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colSpan} className="hc-table-empty"><span className="hc-spinner" style={{ marginRight: 8 }} />Loading...</td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={colSpan} className="hc-table-empty">No data available</td></tr>
            ) : paged.map((row) => {
              const isSelected = selected.some((x) => x.id === row.id);
              return (
                <tr key={row.id} className={isSelected ? 'hc-row-selected' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" className="hc-checkbox" checked={isSelected} onChange={() => onToggle(row)} />
                  </td>
                  <td>{row.crqChangeId}</td>
                  {extraColumns.map((col) => (
                    clickableKeys.includes(col.key)
                      ? <td key={col.key} className={`hc-table-msg${row[col.key] ? ' hc-table-msg-clickable' : ''}`} title={row[col.key]} style={{ maxWidth: 120 }} onClick={() => row[col.key] && onMessageClick(row[col.key])}>{row[col.key]}</td>
                      : <td key={col.key}>{col.isDate ? formatTimestamp(row[col.key]) : row[col.key]}</td>
                  ))}
                  <td style={{ textAlign: 'center' }}>{row.warning ? <span className="hc-table-warn" title="Warning">⚠</span> : ''}</td>
                  <td style={{ textAlign: 'center' }}>{row.error === true ? <span className="hc-table-error" title="Error">✕</span> : (row.error ? <span className="hc-table-error" title={row.error}>✕</span> : '')}</td>
                  <td className={`hc-table-msg${row.message ? ' hc-table-msg-clickable' : ''}`} onClick={() => row.message && onMessageClick(row.message)} title={row.message} style={{ maxWidth: 120 }}>{row.message}</td>
                  <td>{formatTimestamp(row.rpaStart)}</td>
                  <td>{formatTimestamp(row.rpaEnd)}</td>
                  {!hideUser && <td>{row.user}</td>}
                  <td>{row.bot}</td>
                  <td>
                    {row.status === 'ONGOING' && <span className="hc-status-spinning" title="Ongoing" />}
                    {row.status === 'DONE'    && <svg className="hc-status-done"  viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    {row.status === 'ERROR'   && <svg className="hc-status-error" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && filtered.length > PAGE_SIZE && (
        <div className="hc-pagination">
          <button className="hc-page-btn" onClick={() => setPage(1)}                disabled={page === 1}>«</button>
          <button className="hc-page-btn" onClick={() => setPage((p) => p - 1)}    disabled={page === 1}>‹</button>
          <span className="hc-page-info">{page} / {totalPages} <span className="hc-page-total">({filtered.length})</span></span>
          <button className="hc-page-btn" onClick={() => setPage((p) => p + 1)}    disabled={page === totalPages}>›</button>
          <button className="hc-page-btn" onClick={() => setPage(totalPages)}      disabled={page === totalPages}>»</button>
        </div>
      )}
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function HelixOther() {
  const classes          = useStyles();
  const dispatch         = useDispatch();
  const loading          = useSelector((state) => state.helixCrqsReducer.loading);
  const crqsWithConflicts  = useSelector((state) => state.helixCrqsReducer.crqsWithConflicts  ?? []);
  const crqsToAddActivity  = useSelector((state) => state.helixCrqsReducer.crqsToAddActivity  ?? []);
  const botSummary         = useSelector((state) => state.helixCrqsReducer.botSummary);
  const userId             = useSelector((state) => state.session.user?.id);

  const botSummaryRows = botSummary?.helixCrqsBotSummaryUsage       ?? [];
  const botSummaryOps  = botSummary?.helixCrqsBotSummaryOperations?.[0] ?? null;

  const [selectedMessage,       setSelectedMessage]       = useState(null);
  const [panelOpen,             setPanelOpen]             = useState(false);

  const playToggleSound = (opening) => {
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type            = 'sine';
      osc.frequency.value = opening ? 440 : 330;
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    } catch {}
  };
  const [selectedWithConflicts, setSelectedWithConflicts] = useState([]);
  const [selectedToAddActivity, setSelectedToAddActivity] = useState([]);
  const [historyWithConflicts,  setHistoryWithConflicts]  = useState(false);
  const [historyToAddActivity,  setHistoryToAddActivity]  = useState(false);

  const pollingRef               = useRef(null);
  const historyWithConflictsRef  = useRef(historyWithConflicts);
  const historyToAddActivityRef  = useRef(historyToAddActivity);
  useEffect(() => { historyWithConflictsRef.current  = historyWithConflicts;  }, [historyWithConflicts]);
  useEffect(() => { historyToAddActivityRef.current  = historyToAddActivity;  }, [historyToAddActivity]);

  const stopPolling  = () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };

  // ── race-condition guard ─────────────────────────────────────────────────
  const safeGetCrqsWithConflicts = (history) => {
    dispatch(hcGetCrqsWithConflicts(history)).then(() => {
      if (historyWithConflictsRef.current !== history) dispatch(hcGetCrqsWithConflicts(historyWithConflictsRef.current));
    });
  };
  const safeGetCrqsToAddActivity = (history) => {
    dispatch(hcGetCrqsToAddActivity(history)).then(() => {
      if (historyToAddActivityRef.current !== history) dispatch(hcGetCrqsToAddActivity(historyToAddActivityRef.current));
    });
  };
  const safePollCrqsWithConflicts = (history) => {
    dispatch(hcPollCrqsWithConflicts(history)).then(() => {
      if (historyWithConflictsRef.current !== history) dispatch(hcGetCrqsWithConflicts(historyWithConflictsRef.current));
    });
  };
  const safePollCrqsToAddActivity = (history) => {
    dispatch(hcPollCrqsToAddActivity(history)).then(() => {
      if (historyToAddActivityRef.current !== history) dispatch(hcGetCrqsToAddActivity(historyToAddActivityRef.current));
    });
  };

  const startPolling = () => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      safePollCrqsWithConflicts(historyWithConflictsRef.current);
      safePollCrqsToAddActivity(historyToAddActivityRef.current);
      dispatch(hcPollBotSummary());
    }, 5000);
  };

  useEffect(() => {
    safeGetCrqsWithConflicts(historyWithConflicts);
    safeGetCrqsToAddActivity(historyToAddActivity);
    dispatch(hcGetBotSummary());
    startPolling();
    return () => stopPolling();
  }, []);

  useEffect(() => { stopPolling(); safeGetCrqsWithConflicts(historyWithConflicts); startPolling(); }, [historyWithConflicts]);
  useEffect(() => { stopPolling(); safeGetCrqsToAddActivity(historyToAddActivity); startPolling(); }, [historyToAddActivity]);

  const handleToggle = useCallback((setter, row) => {
    setter((prev) =>
      prev.some((x) => x.id === row.id)
        ? prev.filter((x) => x.id !== row.id)
        : [...prev, { id: row.id, crqChangeId: row.crqChangeId }]
    );
  }, []);

  const handleToggleAll = useCallback((setter, rows, selected) => {
    const allSelected = rows.every((r) => selected.some((x) => x.id === r.id));
    setter(allSelected ? [] : rows.map((r) => ({ id: r.id, crqChangeId: r.crqChangeId })));
  }, []);

  const handleWithConflictsAction = useCallback((action, selected, setter, bot = 0) => {
    const idList       = selected.map((x) => x.id);
    const crqChangeIds = selected.map((x) => x.crqChangeId);
    dispatch(hcCrqsWithConflictsAction(userId, action, idList, crqChangeIds, bot)).then(() => {
      setter([]);
      dispatch(hcGetCrqsWithConflicts(historyWithConflicts));
    });
  }, [dispatch, userId, historyWithConflicts]);

  const handleToAddActivityAction = useCallback((action, selected, setter, bot = 0) => {
    const idList       = selected.map((x) => x.id);
    const crqChangeIds = selected.map((x) => x.crqChangeId);
    dispatch(hcCrqsToAddActivityAction(userId, action, idList, crqChangeIds, bot)).then(() => {
      setter([]);
      dispatch(hcGetCrqsToAddActivity(historyToAddActivity));
    });
  }, [dispatch, userId, historyToAddActivity]);

  return (
    <Page className={classes.root} title="Helix Other">
      <Header module="NASP" view="Helix Other" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="hc-root">
            {selectedMessage && <MessageModal message={selectedMessage} onClose={() => setSelectedMessage(null)} />}
            <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── Mini Panel ─────────────────────────────────────────────── */}
              <div className="hc-card">
                <div
                  onClick={() => { playToggleSound(!panelOpen); setPanelOpen((o) => !o); }}
                  style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Bot Summary &amp; Options
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: panelOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                <div style={{ display: 'grid', gridTemplateRows: panelOpen ? '1fr' : '0fr', transition: 'grid-template-rows 0.25s ease' }}>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ display: 'flex', borderTop: '1px solid #f1f5f9' }}>
                      <div style={{ flex: 1, minWidth: 0, borderRight: '1px solid #f1f5f9' }}>
                        <div style={{ padding: '8px 14px 4px', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Usage</span>
                        </div>
                        <div className="hc-table-wrapper" style={{ height: 'auto', overflow: 'visible' }}>
                          <table className="hc-table" style={{ fontSize: 11 }}>
                            <thead>
                              <tr>
                                <th style={{ padding: '5px 10px' }}>Bot</th>
                                <th style={{ padding: '5px 10px' }}>Creations</th>
                                <th style={{ padding: '5px 10px' }}>Changes</th>
                                <th style={{ padding: '5px 10px' }}>Other</th>
                                <th style={{ padding: '5px 10px' }}>Usage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading.botSummary ? (
                                <tr><td colSpan={5} className="hc-table-empty"><span className="hc-spinner" style={{ marginRight: 8 }} />Loading...</td></tr>
                              ) : botSummaryRows.length === 0 ? (
                                <tr><td colSpan={5} className="hc-table-empty">No data available</td></tr>
                              ) : botSummaryRows.map((row, i) => (
                                <tr key={row.bot} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                  <td style={{ padding: '5px 10px' }}>{row.bot}</td>
                                  <td style={{ padding: '5px 10px' }}>{row.creations}</td>
                                  <td style={{ padding: '5px 10px' }}>{row.changes}</td>
                                  <td style={{ padding: '5px 10px' }}>{row.other}</td>
                                  <td style={{ padding: '5px 10px' }}>{row.usage}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div style={{ width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #f1f5f9' }}>
                        <div style={{ padding: '8px 14px 4px', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Totals</span>
                        </div>
                        <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {[
                            { label: 'Creations', value: botSummaryOps?.creations ?? '—', color: '#1d4ed8', bg: '#eff6ff' },
                            { label: 'Changes',   value: botSummaryOps?.changes   ?? '—', color: '#16a34a', bg: '#f0fdf4' },
                            { label: 'Other',     value: botSummaryOps?.other     ?? '—', color: '#7c3aed', bg: '#faf5ff' },
                          ].map((item) => (
                            <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 8, background: item.bg }}>
                              <span style={{ fontSize: 11, fontWeight: 500, color: '#475569', fontFamily: "'Inter', sans-serif" }}>{item.label}</span>
                              <span style={{ fontSize: 16, fontWeight: 700, color: item.color, fontFamily: "'Inter', sans-serif" }}>{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '8px 14px 4px', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Options</span>
                        </div>
                        <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            className="hc-dropdown-trigger"
                            onClick={(e) => { e.stopPropagation(); dispatch(hcExportCrqsOther()); }}
                            disabled={loading.exportCrqsOther}
                            style={{ gap: 6, color: '#16a34a', borderColor: '#bbf7d0' }}
                          >
                            {loading.exportCrqsOther
                              ? <span className="hc-spinner" style={{ width: 12, height: 12 }} />
                              : <span style={{ fontSize: 14 }}>↓</span>
                            }
                            Export
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── CRQ(s) with Conflicts ── */}
              <div className="hc-card">
                <div className="hc-card-header">
                  <span className="hc-card-title">CRQ(s) with Conflicts</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <HistoryToggle value={historyWithConflicts} onChange={setHistoryWithConflicts} />
                    <ChangeBotDropdown
                      selectedIds={selectedWithConflicts}
                      onChangeBot={(bot) => handleWithConflictsAction('ChangeBot', selectedWithConflicts, setSelectedWithConflicts, bot)}
                      disabled={loading.crqsWithConflictsAction || historyWithConflicts}
                    />
                  </div>
                </div>
                <div className="hc-card-body" style={{ padding: 0 }}>
                  <CrqTable
                    rows={crqsWithConflicts}
                    loading={loading.crqsWithConflicts}
                    onMessageClick={setSelectedMessage}
                    selected={selectedWithConflicts}
                    onToggle={(row) => handleToggle(setSelectedWithConflicts, row)}
                    onToggleAll={(rows) => handleToggleAll(setSelectedWithConflicts, rows, selectedWithConflicts)}
                    extraColumns={[{ key: 'crqChangeIdConflict', label: 'CRQ Change ID (Conflict)' }]}
                    hideUser
                  />
                </div>
              </div>

              {/* ── CRQ(s) to Add Activity ── */}
              <div className="hc-card">
                <div className="hc-card-header">
                  <span className="hc-card-title">CRQ(s) to Add Activity</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <HistoryToggle value={historyToAddActivity} onChange={setHistoryToAddActivity} />
                    <ChangeBotDropdown
                      selectedIds={selectedToAddActivity}
                      onChangeBot={(bot) => handleToAddActivityAction('ChangeBot', selectedToAddActivity, setSelectedToAddActivity, bot)}
                      disabled={loading.crqsToAddActivityAction || historyToAddActivity}
                    />
                  </div>
                </div>
                <div className="hc-card-body" style={{ padding: 0 }}>
                  <CrqTable
                    rows={crqsToAddActivity}
                    loading={loading.crqsToAddActivity}
                    onMessageClick={setSelectedMessage}
                    selected={selectedToAddActivity}
                    onToggle={(row) => handleToggle(setSelectedToAddActivity, row)}
                    onToggleAll={(rows) => handleToggleAll(setSelectedToAddActivity, rows, selectedToAddActivity)}
                    extraColumns={[
                      { key: 'task',     label: 'Task'     },
                      { key: 'activity', label: 'Activity' },
                    ]}
                    clickableKeys={['activity']}
                  />
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default HelixOther;